<template>
  <div
    ref="map"
    class="stores-map"
  >
    <template v-if="popupByClick">
      <vue-client-only>
        <modal
          v-if="!adaptive.isDesktop"
          :visible="!storeCardIsEmpty"
          @on-close="toggleStoreCard()"
        >
          <store-card
            :cart-button="cartButtonIsActive"
            :store="activeStoreCard"
          />
        </modal>
        <store-card
          v-else
          :cart-button="cartButtonIsActive"
          :store="activeStoreCard"
          @close="toggleStoreCard()"
        />
      </vue-client-only>
    </template>

    <div
      v-if="!isLoaded"
      class="map-loader"
    >
      <ui-loader
        size="56"
      />
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import VueClientOnly from 'vue-client-only';
/* Maps */
import ymaps from 'ymaps';
import { activeMarkerOptions, defaultMarkerOptions, yaKey } from '@/constants/maps';
import { pickupMarkerOptions, createBalloon } from '@/utils/map-utils';

/* Components */
import StoreCard from './store-card';

export default {
  name: 'stores-map',

  components: {
    StoreCard,
    VueClientOnly,
  },

  inject: ['adaptive'],

  props: {
    // Активирует отображение попапов/карточек внутри карты
    popupByClick: {
      type: Boolean,
      default: false
    },

    // Активирует функционал "Забрать в магазине"
    cartButtonIsActive: {
      type: Boolean,
      default: false
    },

    stores: {
      type: Array,
      default: () => [],
    },

    currentLocalCity: {
      type: Object,
      default: () => {},
    },

    activeStoreId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isLoaded: false,
      map: null,
      clusterer: null,
      activeStoreMarker: null,
    };
  },

  computed: {
    ...mapGetters({
      CITY: 'user/city',
    }),

    activeStoreCard() { // определяет объект активного магазина по id
      return this.stores.find(store => store.id === this.activeStoreId) || {};
    },

    storeCardIsEmpty() {
      return Object.keys(this.activeStoreCard).length === 0;
    }
  },

  watch: {
    stores(val) { // обновление магазинов на карте
      if (this.isLoaded) {
        this.map.geoObjects.removeAll();
        val.length ? this.addStores(val) : this.centerCityToDefault();
      }
    },

    activeStoreId() { // выбор активного магазина
      this.activeStoreCard?.geo ? this.centerMarker(this.activeStoreCard.geo) : this.centerCityToDefault();
    }
  },

  mounted() {
    if (!this.isLoaded) {
      this.initMap();
    }
  },

  beforeDestroy() {
    this.map?.destroy();
  },

  methods: {
    initMap() {
      ymaps.load(`//api-maps.yandex.ru/2.1/?apikey=${yaKey}&lang=ru_RU`)
          .then(maps => {
            this.isLoaded = true;

            this.api = window.ymaps;

            this.map = new maps.Map(this.$refs.map, {
              center: Object.values(this.CITY.geo), // центр карты по умолчанию город клиента
              zoom: this.adaptive.isDesktop ? 8 : 6,
              controls: [],
            }, {
              maxZoom: 18
            });
            this.addStores(this.stores);
            if (this.adaptive.isDesktop) this.addControlButtons();

            console.log('Map is loaded');
          })
          .catch(error => console.log('Failed to load Yandex Maps', error));
    },

    addControlButtons() { // кнпоки зума и опр. гео
      if (!this.map) return

      this.map.controls.add('geolocationControl', {
        float: 'none',
        position: {
          bottom: '42px',
          right: '16px',
        },
      });
      this.map.controls.add('zoomControl', {
        float: 'none',
        size: 'small',
        position: {
          bottom: '90px',
          right: '16px',
        },
      });
    },

    addStores(stores) {
      if (!stores?.length) return;

      let marker;
      let storeMarkers = [];

      stores.forEach(store => {
        marker = new this.api.Placemark([store.geo.latitude, store.geo.longitude], {
          defaultOptions: defaultMarkerOptions, // записываем в properties маркера необх. данные
          activeOptions: activeMarkerOptions,
          contentData: {
            id: store.id
          },
        },
          {
            ...this.popupByClick
              ? defaultMarkerOptions
              : pickupMarkerOptions({context: this.api, count: store.availableLinesCount, icon: store.type}),
            ...createBalloon(this.api, store.balloon),
          }
        );

        storeMarkers.push(marker);
      })

      let markersCollection = this.api // переводим коллекцию в GeoQueryResult
          .geoQuery(storeMarkers)
          .addEvents('click', (e) => {
            if (this.popupByClick) {
              this.activateMarker(e.get('target'));
            } else {
              this.$emit('select', this.getMarkerProperties(e.get('target'),'contentData').id);
            }
          });

      this.initClusterer(markersCollection);
    },

    initClusterer(markers) { // формат markers - GeoQueryResult
      this.clusterer = markers.clusterize({
        preset: 'islands#darkBlueClusterIcons', // стили кластера
        gridSize: 50, // чтобы маркеры не наезжали друг на друга
        zoomMargin: 50, // отступы до краев карты при зуме
      });

      this.map.geoObjects.add(this.clusterer);

      this.map.setBounds(this.clusterer.getBounds(), {
        checkZoomRange: true,
      });
    },

    activateMarker(marker) {
      if (this.activeStoreMarker) this.setMarkerOptions(this.activeStoreMarker, 'defaultOptions'); // деактивируем старый маркер
      this.activeStoreMarker = marker;
      this.setMarkerOptions(marker, 'activeOptions'); // активируем новый

      this.toggleStoreCard(marker);
    },

    toggleStoreCard(marker = null) { // управление карточкой внутри карты
      if (!marker) {
        this.$emit('change', '')
        this.setMarkerOptions(this.activeStoreMarker, 'defaultOptions');
      } else {
        this.$emit('change', this.getMarkerProperties(marker,'contentData').id); // получаем данные для карточки
      }
    },

    centerCityToDefault() {
      let geo = this.currentLocalCity?.geo || this.CITY?.geo;
      this.map?.setCenter([geo.latitude, geo.longitude], 10);
    },

    centerMarker(coords) {
      this.map?.setCenter([coords.latitude, coords.longitude], 17);
    },

    getMarkerProperties(marker, property) {   // объект опций должен быть предварительно
      if (!marker) return;
      return marker.properties.get(property); // добавлен в properties маркера
    },

    setMarkerOptions(marker, setOptions = 'defaultOptions') { // метод перезаписи options маркера
      if (!marker) return;
      marker?.options?.set(this.getMarkerProperties(marker, setOptions));
    }
  }
};
</script>

<style lang="scss">
.stores-map {
  position: relative;
  height: 100%;

  .map-loader {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: $blue;
  }
}

.custom-balloon {
  position: relative;
  padding: 24px;
  width: 320px;
  box-shadow: 0 8px 40px rgba(51, 51, 51, .16);
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  line-height: 24px;
  color: #000;

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
    cursor: pointer;
  }

  &__label {
    margin-bottom: 4px;
    line-height: 16px;
    color: $gray;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
  }

  &__block:not(:last-child) {
    margin-bottom: 12px;
  }
}

@include breakpoint("tablet") {
  .stores-map {
    .modal {
      .modal-content {
        width: 320px;
      }
    }
  }
}

@include breakpoint("desktop") {
  .stores-map {
    .modal {
      position: absolute;
      top: 32px;
      left: 32px;
      right: auto;
      bottom: auto;
      background: transparent;
    }

    .modal-content {
      padding: 32px 24px;
    }
  }
}
</style>
